import { Alert, Box, Button, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { isTotalledLine, useQuoteTotals } from "../../api/useQuotes";
import { IQuoteLineItem } from "../../shared/types";
import "./QuoteTotalsPanel.scss";

type Props = {
  // The active section's lines, as priced by the editor's PreviewPrices.
  items: IQuoteLineItem[];
  // The editor is repricing (an edit is waiting on, or being sent to, PreviewPrices) - line
  // prices are stale or blank until it returns, so the last totals stay up, marked "Updating...".
  pending: boolean;
  // PreviewPrices failed - the lines' prices can't be trusted, so no totals are shown.
  pricingFailed: boolean;
};

const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

// The quote editor's live Totals table (2026-10-05) - the customer PDF's Totals rows (Label +
// Amount, no commission), computed by the server with the same code as the documents, so staff
// see a quote's totals before finalizing it.
const QuoteTotalsPanel = ({ items, pending, pricingFailed }: Props) => {
  const totals = useQuoteTotals(items, !pending && !pricingFailed);
  const pricedCount = items.filter(isTotalledLine).length;
  if (pricedCount === 0) return null;

  if (pricingFailed || totals.isError) return <Box className="quote-totals-panel">
    <Alert severity="warning" action={totals.isError && !pricingFailed
      ? <Button color="inherit" size="small" onClick={() => totals.refetch()}>Retry</Button> : undefined}>
      {pricingFailed ? "Totals are unavailable until prices are calculated." : "Totals could not be calculated."}
    </Alert>
  </Box>;

  if (!totals.data) return <Box className="quote-totals-panel quote-totals-status">
    <CircularProgress size={18} /><Typography variant="body2">Calculating totals...</Typography>
  </Box>;

  const updating = pending || totals.isFetching;
  const unpriced = items.length - pricedCount;
  return <Box className="quote-totals-panel">
    <Table size="small" aria-label="Quote totals" className={updating ? "quote-totals-table quote-totals-updating" : "quote-totals-table"}>
      <TableHead>
        <TableRow className="quote-totals-header">
          <TableCell>Totals</TableCell>
          <TableCell align="right">Amount</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {totals.data.rows.map(row => <TableRow key={row.label} className={row.is_grand_total ? "quote-totals-grand" : undefined}>
          <TableCell>{row.label}</TableCell>
          <TableCell align="right" className="quote-totals-amount">{money.format(row.amount)}</TableCell>
        </TableRow>)}
      </TableBody>
    </Table>
    {updating && <Box className="quote-totals-status" role="status">
      <CircularProgress size={14} /><Typography variant="caption">Updating...</Typography>
    </Box>}
    {!updating && unpriced > 0 && <Typography variant="caption" color="text.secondary" className="quote-totals-note">
      {unpriced === 1 ? "1 line without a price is not included." : `${unpriced} lines without a price are not included.`}
    </Typography>}
  </Box>;
};

export default QuoteTotalsPanel;
